import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';

function normalizeDirection(direction) {
  if (direction === 'token_to_itani' || direction === 'reverse') {
    return 'token_to_itani';
  }

  return 'itani_to_token';
}

function pickPools(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.pools)) {
    return response.pools;
  }

  if (Array.isArray(response?.data?.pools)) {
    return response.data.pools;
  }

  return [];
}

function pickOracleData(response) {
  if (!response) {
    return {};
  }

  return response.data || response;
}

function pickQuote(response) {
  return response?.data || response || null;
}

function normalizeSwapArgs(args, defaults) {
  const [first, second, third, fourth] = args;

  if (first && typeof first === 'object' && !Array.isArray(first)) {
    return first;
  }

  return {
    token_in: first,
    token_out: second,
    amount: third,
    address: fourth,
    pool_id: defaults.selectedPool || second,
    asset: second || defaults.selectedToken,
    direction: defaults.direction,
  };
}

export function useDEX(initialState = {}) {
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState(initialState.selectedPool || '');
  const [selectedToken, setSelectedToken] = useState(initialState.selectedToken || initialState.asset || 'HUDL');
  const [amount, setAmount] = useState(initialState.amount || '');
  const [direction, setDirectionState] = useState(
    normalizeDirection(initialState.direction || 'itani_to_token'),
  );
  const [quote, setQuote] = useState(null);
  const [swapResult, setSwapResult] = useState(null);
  const [oracleData, setOracleData] = useState({});
  const [loading, setLoading] = useState(true);
  const [quoting, setQuoting] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [error, setError] = useState('');

  const setDirection = useCallback((value) => {
    setDirectionState(normalizeDirection(value));
  }, []);

  const fetchPools = useCallback(async () => {
    const response = await api.exchange.pools();
    const nextPools = pickPools(response);
    setPools(nextPools);

    if (!selectedPool && nextPools.length > 0) {
      setSelectedPool(nextPools[0]?.symbol || nextPools[0]?.asset || nextPools[0]?.id || '');
    }

    return nextPools;
  }, [selectedPool]);

  const fetchOraclePrice = useCallback(async () => {
    const response = await api.oracle.price();
    const nextOracle = pickOracleData(response);
    setOracleData(nextOracle);
    return nextOracle;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [nextPools, nextOracle] = await Promise.all([fetchPools(), fetchOraclePrice()]);
      return { pools: nextPools, oracle: nextOracle };
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load DEX data.';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchOraclePrice, fetchPools]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const quoteSwap = useCallback(
    async (...args) => {
      const payload = normalizeSwapArgs(args, { selectedPool, selectedToken, direction });
      const request = {
        amount: payload.amount ?? amount,
        asset: payload.asset ?? payload.token ?? payload.token_out ?? selectedToken,
        pool: payload.pool ?? payload.pool_id ?? selectedPool,
        pool_id: payload.pool_id ?? payload.pool ?? selectedPool,
        direction: normalizeDirection(payload.direction ?? direction),
        from_address: payload.from_address ?? payload.address,
        address: payload.address ?? payload.from_address,
      };

      setQuoting(true);
      setError('');

      try {
        const response = await api.exchange.quote(request);
        const nextQuote = pickQuote(response);
        setQuote(nextQuote);
        return nextQuote;
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || 'Unable to fetch DEX quote.';
        setError(message);
        throw err;
      } finally {
        setQuoting(false);
      }
    },
    [amount, direction, selectedPool, selectedToken],
  );

  const executeSwap = useCallback(
    async (...args) => {
      const payload = normalizeSwapArgs(args, { selectedPool, selectedToken, direction });
      const request = {
        amount: payload.amount ?? amount,
        asset: payload.asset ?? payload.token ?? payload.token_out ?? selectedToken,
        pool: payload.pool ?? payload.pool_id ?? selectedPool,
        pool_id: payload.pool_id ?? payload.pool ?? selectedPool,
        direction: normalizeDirection(payload.direction ?? direction),
        from_address: payload.from_address ?? payload.address,
        to_address: payload.to_address,
        address: payload.address ?? payload.from_address,
        quote_id: payload.quote_id || quote?.quote_id,
      };

      setSwapping(true);
      setError('');

      try {
        const response = await api.exchange.swap(request);
        const result = pickQuote(response);
        setSwapResult(result);
        return result;
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || 'Unable to execute swap.';
        setError(message);
        throw err;
      } finally {
        setSwapping(false);
      }
    },
    [amount, direction, quote, selectedPool, selectedToken],
  );

  const oraclePriceUsd = useMemo(
    () => oracleData?.spot_usd ?? oracleData?.price_usd ?? oracleData?.usd ?? oracleData?.price ?? null,
    [oracleData],
  );

  const oraclePriceEur = useMemo(
    () => oracleData?.spot_eur ?? oracleData?.price_eur ?? oracleData?.eur ?? null,
    [oracleData],
  );

  return {
    pools,
    selectedPool,
    setSelectedPool,
    selectedToken,
    setSelectedToken,
    asset: selectedToken,
    setAsset: setSelectedToken,
    amount,
    setAmount,
    direction,
    setDirection,
    quote,
    setQuote,
    swapResult,
    setSwapResult,
    oracleData,
    oracle: oracleData,
    priceData: oracleData,
    oraclePrice: oraclePriceUsd,
    oraclePriceUsd,
    oraclePriceEur,
    loading,
    quoting,
    swapping,
    refreshing: loading,
    error,
    refresh,
    reload: refresh,
    fetchPools,
    fetchOraclePrice,
    refreshPools: fetchPools,
    getPrice: fetchOraclePrice,
    quoteSwap,
    requestQuote: quoteSwap,
    executeSwap,
    swap: executeSwap,
    price: oraclePriceUsd,
    normalizeDirection,
  };
}

export default useDEX;
